/**
 * RL Loop API Service
 *
 * API functions for the RL (Reinforcement Learning) Loop feature.
 * Manages automated iterative improvement cycles for therapist prompts.
 */

import apiClient, { rawApiClient } from './api';

// ============ Loop Management ============

/**
 * Start a new RL loop
 * @param {Object} config - Loop configuration
 * @param {string} token - Auth token
 */
export const startRLLoop = async (config, token) => {
  try {
    const response = await apiClient.post('/advanced-simulation/rl-loop', config, token);
    return response.data;
  } catch (error) {
    console.error('Error starting RL loop:', error);
    throw error.response?.data || error;
  }
};

/**
 * List all RL loops
 * @param {Object} filters - Query filters
 * @param {string} token - Auth token
 */
export const listRLLoops = async (filters = {}, token) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const response = await apiClient.get(`/advanced-simulation/rl-loop?${params.toString()}`, token);
    return response.data;
  } catch (error) {
    console.error('Error listing RL loops:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get loop status
 * @param {string} loopId - Loop ID
 * @param {string} token - Auth token
 */
export const getRLLoopStatus = async (loopId, token) => {
  try {
    const response = await apiClient.get(`/advanced-simulation/rl-loop/${loopId}`, token);
    return response.data;
  } catch (error) {
    console.error('Error getting RL loop status:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get all iterations for a loop
 * @param {string} loopId - Loop ID
 * @param {string} token - Auth token
 */
export const getRLLoopIterations = async (loopId, token) => {
  try {
    const response = await apiClient.get(`/advanced-simulation/rl-loop/${loopId}/iterations`, token);
    return response.data;
  } catch (error) {
    console.error('Error getting RL loop iterations:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get a specific iteration
 * @param {string} loopId - Loop ID
 * @param {number} iterationNumber - Iteration number
 * @param {string} token - Auth token
 */
export const getRLLoopIteration = async (loopId, iterationNumber, token) => {
  try {
    const response = await apiClient.get(
      `/advanced-simulation/rl-loop/${loopId}/iterations/${iterationNumber}`,
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error getting RL loop iteration:', error);
    throw error.response?.data || error;
  }
};

// ============ Loop Control ============

/**
 * Pause a running loop
 * @param {string} loopId - Loop ID
 * @param {string} reason - Pause reason
 * @param {string} token - Auth token
 */
export const pauseRLLoop = async (loopId, reason, token) => {
  try {
    const response = await apiClient.post(
      `/advanced-simulation/rl-loop/${loopId}/pause`,
      { reason },
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error pausing RL loop:', error);
    throw error.response?.data || error;
  }
};

/**
 * Resume a paused loop
 * @param {string} loopId - Loop ID
 * @param {string} userNotes - Notes about changes made during pause
 * @param {string} token - Auth token
 */
export const resumeRLLoop = async (loopId, userNotes, token) => {
  try {
    const response = await apiClient.post(
      `/advanced-simulation/rl-loop/${loopId}/resume`,
      { userNotes },
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error resuming RL loop:', error);
    throw error.response?.data || error;
  }
};

/**
 * Cancel a loop
 * @param {string} loopId - Loop ID
 * @param {string} token - Auth token
 */
export const cancelRLLoop = async (loopId, token) => {
  try {
    const response = await apiClient.post(
      `/advanced-simulation/rl-loop/${loopId}/cancel`,
      {},
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error cancelling RL loop:', error);
    throw error.response?.data || error;
  }
};

/**
 * Rollback to a previous iteration
 * @param {string} loopId - Loop ID
 * @param {number} iterationNumber - Target iteration number
 * @param {string} token - Auth token
 */
export const rollbackRLLoop = async (loopId, iterationNumber, token) => {
  try {
    const response = await apiClient.post(
      `/advanced-simulation/rl-loop/${loopId}/rollback/${iterationNumber}`,
      {},
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error rolling back RL loop:', error);
    throw error.response?.data || error;
  }
};

// ============ Promotion ============

/**
 * Promote sandbox personas to live
 * @param {string} loopId - Loop ID
 * @param {Array} personaKeys - Array of persona keys to promote (optional)
 * @param {string} token - Auth token
 */
export const promoteRLLoopPersonas = async (loopId, personaKeys = [], token) => {
  try {
    const response = await apiClient.post(
      `/advanced-simulation/rl-loop/${loopId}/promote`,
      { personaKeys },
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error promoting RL loop personas:', error);
    throw error.response?.data || error;
  }
};

/**
 * Save improved prompt as a new persona
 * @param {string} loopId - Loop ID
 * @param {Object} data - New persona details
 * @param {string} data.personaKey - Original persona key
 * @param {string} data.newKey - Key for new persona
 * @param {string} data.newName - Name for new persona
 * @param {string} [data.newDescription] - Optional description
 * @param {string} token - Auth token
 */
export const saveRLLoopAsNewPersona = async (loopId, data, token) => {
  try {
    const response = await apiClient.post(
      `/advanced-simulation/rl-loop/${loopId}/save-as-new`,
      data,
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error saving RL loop as new persona:', error);
    throw error.response?.data || error;
  }
};

// ============ Version History & Export ============

/**
 * Get prompt version history for a loop
 * @param {string} loopId - Loop ID
 * @param {string} token - Auth token
 */
export const getRLLoopVersionHistory = async (loopId, token) => {
  try {
    const response = await apiClient.get(
      `/advanced-simulation/rl-loop/${loopId}/version-history`,
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error getting RL loop version history:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get prompt comparison between sandbox and original persona
 * @param {string} loopId - Loop ID
 * @param {string} token - Auth token
 */
export const getRLLoopPromptComparison = async (loopId, token) => {
  try {
    const response = await apiClient.get(
      `/advanced-simulation/rl-loop/${loopId}/prompt-comparison`,
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error getting RL loop prompt comparison:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get session summary for a completed loop
 * @param {string} loopId - Loop ID
 * @param {boolean} regenerate - Force regeneration of summary
 * @param {string} token - Auth token
 */
export const getRLLoopSessionSummary = async (loopId, regenerate = false, token) => {
  try {
    const params = regenerate ? '?regenerate=true' : '';
    const response = await apiClient.get(
      `/advanced-simulation/rl-loop/${loopId}/session-summary${params}`,
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error getting RL loop session summary:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get export configuration for a loop
 * @param {string} loopId - Loop ID
 * @param {string} token - Auth token
 */
export const getRLLoopExportConfig = async (loopId, token) => {
  try {
    const response = await apiClient.get(
      `/advanced-simulation/rl-loop/${loopId}/export-config`,
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error getting RL loop export config:', error);
    throw error.response?.data || error;
  }
};

/**
 * Export loop results
 * @param {string} loopId - Loop ID
 * @param {Object} options - Export options
 * @param {string} options.format - Export format (json, markdown, or pdf)
 * @param {boolean} options.includeTranscripts - Include simulation transcripts
 * @param {boolean} options.includePromptChanges - Include prompt change history
 * @param {boolean} options.includeMetrics - Include quality metrics
 * @param {boolean} options.includeReviews - Include iteration reviews
 * @param {boolean} options.includeCosts - Include cost information
 * @param {string} token - Auth token
 */
export const exportRLLoop = async (loopId, options = {}, token) => {
  try {
    const {
      format = 'json',
      includeTranscripts = false,
      includePromptChanges = true,
      includeMetrics = true,
      includeReviews = true,
      includeCosts = true
    } = options;

    const params = new URLSearchParams({
      format,
      includeTranscripts: String(includeTranscripts),
      includePromptChanges: String(includePromptChanges),
      includeMetrics: String(includeMetrics),
      includeReviews: String(includeReviews),
      includeCosts: String(includeCosts)
    });

    // For PDF, we need to handle blob response using rawApiClient
    if (format === 'pdf') {
      const response = await rawApiClient.get(
        `/advanced-simulation/rl-loop/${loopId}/export?${params.toString()}`,
        {
          headers: { 'x-auth-token': token },
          responseType: 'blob'
        }
      );
      return response.data; // Returns blob directly
    }

    // For JSON and Markdown
    const response = await apiClient.get(
      `/advanced-simulation/rl-loop/${loopId}/export?${params.toString()}`,
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error exporting RL loop:', error);
    throw error.response?.data || error;
  }
};

/**
 * Update loop configuration (e.g., particularFocusText mid-loop)
 * @param {string} loopId - Loop ID
 * @param {Object} configUpdates - Config fields to update
 * @param {string} token - Auth token
 */
export const updateRLLoopConfig = async (loopId, configUpdates, token) => {
  try {
    const response = await apiClient.patch(
      `/advanced-simulation/rl-loop/${loopId}/config`,
      configUpdates,
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error updating RL loop config:', error);
    throw error.response?.data || error;
  }
};

/**
 * Delete a loop
 * @param {string} loopId - Loop ID
 * @param {string} token - Auth token
 */
export const deleteRLLoop = async (loopId, token) => {
  try {
    const response = await apiClient.delete(
      `/advanced-simulation/rl-loop/${loopId}`,
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting RL loop:', error);
    throw error.response?.data || error;
  }
};

// ============ Persona Version Control ============

/**
 * Get version history for a persona
 * @param {string} personaId - Persona ID
 * @param {string} token - Auth token
 */
export const getPersonaVersions = async (personaId, token) => {
  try {
    const response = await apiClient.get(
      `/advanced-simulation/personas/${personaId}/versions`,
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error getting persona versions:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get details of a specific version
 * @param {string} personaId - Persona ID
 * @param {number} versionNumber - Version number
 * @param {string} token - Auth token
 */
export const getPersonaVersionDetail = async (personaId, versionNumber, token) => {
  try {
    const response = await apiClient.get(
      `/advanced-simulation/personas/${personaId}/versions/${versionNumber}`,
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error getting persona version detail:', error);
    throw error.response?.data || error;
  }
};

/**
 * Rollback a persona to a specific version
 * @param {string} personaId - Persona ID
 * @param {number} versionNumber - Target version number
 * @param {string} personaType - 'therapist' or 'client'
 * @param {string} token - Auth token
 */
export const rollbackPersonaVersion = async (personaId, versionNumber, personaType, token) => {
  try {
    const response = await apiClient.post(
      `/advanced-simulation/personas/${personaId}/rollback/${versionNumber}`,
      { personaType },
      token
    );
    return response.data;
  } catch (error) {
    console.error('Error rolling back persona version:', error);
    throw error.response?.data || error;
  }
};

export default {
  // Loop management
  startRLLoop,
  listRLLoops,
  getRLLoopStatus,
  getRLLoopIterations,
  getRLLoopIteration,
  // Loop control
  pauseRLLoop,
  resumeRLLoop,
  cancelRLLoop,
  rollbackRLLoop,
  // Loop config
  updateRLLoopConfig,
  // Promotion
  promoteRLLoopPersonas,
  saveRLLoopAsNewPersona,
  // Version history & export
  getRLLoopVersionHistory,
  getRLLoopPromptComparison,
  getRLLoopExportConfig,
  exportRLLoop,
  deleteRLLoop,
  // Persona version control
  getPersonaVersions,
  getPersonaVersionDetail,
  rollbackPersonaVersion
};
